import type { FloorType } from '../models/floor/floor'

import { byOrder } from './configurator-groups'

/** Structural subset of a floor needed to derive its position. */
export type FloorForPosition = {
  id: number
  type: FloorType
  sortOrder: number
}

export type FloorPosition = {
  type: FloorType
  /** 1-based rank within the floor's own type, following creation order. */
  ordinal: number
  /** How many floors of this type the property has. */
  count: number
  /**
   * Signed storey number exposed to formulas as `roomFloorLevel`, and the
   * key the tree is rendered by (descending = top of the building first).
   * Ground floor is 0, upper floors count up, basements count down, attics
   * continue above the topmost full floor.
   */
  level: number
}

/**
 * Derives each floor's position from the property's floor list. Callers pass
 * every floor of one property.
 *
 * Rank inside a type follows `sortOrder`, which is assigned once at creation
 * and never edited — the first basement is the one directly below ground, the
 * first full floor is the ground floor, the first attic is the lowest one.
 * Adding a floor therefore extends its type outwards from the ground floor.
 */
export function buildFloorPositions(
  floors: ReadonlyArray<FloorForPosition>,
): Map<number, FloorPosition> {
  const ordered = [...floors].sort(byOrder)
  const basements = ordered.filter(f => f.type === 'BASEMENT')
  const fullFloors = ordered.filter(f => f.type === 'FULL_FLOOR')
  const attics = ordered.filter(f => f.type === 'ATTIC')

  const positions = new Map<number, FloorPosition>()
  const assign = (
    list: FloorForPosition[],
    type: FloorType,
    levelOf: (index: number) => number,
  ) => {
    list.forEach((floor, i) => {
      positions.set(floor.id, {
        type,
        ordinal: i + 1,
        count: list.length,
        level: levelOf(i),
      })
    })
  }

  assign(basements, 'BASEMENT', i => -(i + 1))
  assign(fullFloors, 'FULL_FLOOR', i => i)
  assign(attics, 'ATTIC', i => fullFloors.length + i)

  return positions
}

/**
 * Orders floors the way a building is drawn: top storey first, basement last.
 * Descending `level` yields attics (highest first), then upper floors down to
 * the ground floor, then basements from the shallowest down.
 *
 * The order is fully determined by type + creation rank — floors are not
 * user-sortable, so this is the single place that defines the stack.
 */
export function sortFloorsTopDown<T extends FloorForPosition>(
  floors: ReadonlyArray<T>,
): T[] {
  const positions = buildFloorPositions(floors)
  return [...floors].sort(
    (a, b) =>
      (positions.get(b.id)?.level ?? 0) - (positions.get(a.id)?.level ?? 0),
  )
}

/**
 * Projects a room's `floorId` into the two floor-derived formula inputs
 * (`roomFloor`, `roomFloorLevel`). Both are positional, so they can only be
 * resolved against the property's full floor list.
 */
export function floorFormulaFields(
  floorId: number | null | undefined,
  positions: Map<number, FloorPosition>,
): { floorType: string | null; floorLevel: number | null } {
  const position = floorId == null ? undefined : positions.get(floorId)
  if (!position) return { floorType: null, floorLevel: null }
  return { floorType: position.type, floorLevel: position.level }
}

export function countFloorsOfType(
  floors: ReadonlyArray<{ type: FloorType }>,
  type: FloorType,
): number {
  return floors.filter(f => f.type === type).length
}

/** Count that Planning totals and the Objektdaten mismatch hint compare. */
export function countFullFloors(
  floors: ReadonlyArray<{ type: FloorType }>,
): number {
  return countFloorsOfType(floors, 'FULL_FLOOR')
}
